import { CloseOutlined } from '@ant-design/icons';
import { useViewActions } from '@mm/state/actions/viewActions';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './GoogleAdsIntegrationEditorSlideout.module.css';
import { Button, Heading, Status, Select } from '@mm/components';
import { Radio } from 'antd';
import TextInput from '../../../../../common/components/TextInput';
import AdAudienceModel from '@mm/state/model/AdAudienceModel';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import moment from 'moment/moment';
import { colors } from '@mm/utils';
import { SendDataConfig } from '@mm/state/model/WidgetModel';
import { selectWidgetFacebookAudienceConnections } from '@mm/state/selectors/connectionsSelectors';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import classNames from 'classnames';
import { capitalize, isNumber } from 'lodash';
import { formatDateTime } from '../../../../../common/helpers/formatDateTime';
import { useGoogleAdsCRUDActions } from '@mm/state/actions/GoogleAdsCRUDActions';
import {
  selectGoogleAdAudience,
  selectGoogleClientCredentials,
} from '@mm/state/selectors/googleAdsSelectors';
import { GoogleClientCredential } from '@mm/state/interfaces/googleAds';
import { FacebookAdsAudienceMapping } from '../FacebookAdsIntegrationEditorSlideout/FacebookAdsAudienceMapping';
import { GoogleAds } from '../../../../features/beta/modules/GoogleAds/index';

export interface GoogleAdsIntegrationEditorSlideoutProps {
  widgetId?: number;
  adAudienceId?: number;
  adAudienceRemoteId?: number;
  showConfigure?: boolean;
}

export const renderAdAudienceStatus = (adAudience: AdAudienceModel) => {
  if (adAudience.synchronizationStatus === 'not_synchronized') {
    return (
      <Status
        className={styles.status}
        indicatorSizePx={8}
        status="Not Ready"
        color={colors.grayLight}
      />
    );
  }

  if (adAudience.synchronizationStatus === 'stopped') {
    return (
      <Status
        indicatorSizePx={8}
        className={styles.status}
        status="Stopped"
        color={colors.grayLight}
      />
    );
  }

  if (adAudience.synchronizationStatus === 'started') {
    return (
      <Status
        className={styles.status}
        indicatorSizePx={8}
        status="Started"
        color={colors.greenLighter}
      />
    );
  }

  if (adAudience.lastSynchronizationTime) {
    return (
      <Status
        className={styles.status}
        indicatorSizePx={8}
        status="Synced"
        color={colors.green6}
      />
    );
  }
};

export const renderAdCampaignStatus = (campaignStatus: string) => {
  const status = campaignStatus.toLowerCase();

  switch (status) {
    case 'active': {
      return (
        <Status
          className={styles.status}
          indicatorSizePx={8}
          color={colors.green6}
          status={capitalize(status)}
        />
      );
    }
    default:
      return (
        <Status
          className={styles.status}
          indicatorSizePx={8}
          color={colors.grayLight}
          status={capitalize(status)}
        />
      );
  }
};

const renderAdAudienceSyncError = (adAudience: AdAudienceModel) => {
  if (
    !adAudience.synchronizationErrors ||
    adAudience.synchronizationErrors === '{}'
  ) {
    return '';
  }

  try {
    const parsed = JSON.parse(adAudience.synchronizationErrors);
    if (typeof parsed.pull_remote_data === 'string') {
      return parsed.pull_remote_data;
    }

    return adAudience.synchronizationErrors;
  } catch (err) {
    return '';
  }
};

export const renderAdAccountName = (a: GoogleClientCredential) =>
  `${a.id} - ${a.clientId || ''} - (${a.user.email})`;

export const GoogleAdsIntegrationEditorSlideout = ({
  widgetId,
  adAudienceId: adAudienceIdProp,
  adAudienceRemoteId,
  showConfigure,
}: GoogleAdsIntegrationEditorSlideoutProps) => {
  const [adAudienceId, setAdAudienceId] = useState(adAudienceIdProp);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

  const googleClientCredentials = useSelector(selectGoogleClientCredentials);

  const [selectedCredential, setSelectedCredential] = useState<
    (GoogleClientCredential & { label: string })[]
  >([]);

  const adAudience = useSelector((state: StoreState) =>
    selectGoogleAdAudience(state, adAudienceId),
  );

  const chatbotId = useSelector(activeChatbotIdSelector);

  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === widgetId)!;

  const config = widget?.config as Partial<SendDataConfig>;

  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const connections =
    useSelector(selectWidgetFacebookAudienceConnections) ?? [];

  const viewActions = useViewActions();

  const connection = connections.find(
    c => c.id === config?.connectionWebhookId,
  );

  const { setRef } = useSlideoutOnClose();

  const onClose = async () => {
    viewActions.hideSlideoutMenu();
  };

  const handleStatusChange = (synchronizeAutomatically: boolean) => {
    if (adAudience) {
      googleAdsCRUDActions.updateAdAudienceById(facebookPageId, adAudience.id, {
        synchronizeAutomatically,
      });
    }
  };

  const handleAudienceChange = (audienceId: number) => {
    if (adAudience) {
      googleAdsCRUDActions.updateAdAudienceById(facebookPageId, adAudience.id, {
        audienceId,
      });
    }
  };

  const [name, setAdAudienceName] = useState('');
  const [descriprtion, setAdAudienceDescription] = useState('');

  const handleSyncNow = async (aad: AdAudienceModel) => {
    try {
      const res = await googleAdsCRUDActions.syncAudienceById(
        facebookPageId,
        aad.id,
      );
      if (res.adAudience) {
        successToast('Synchronization successfully launched');
      }
    } catch (err) {
      errorToast('Can not start synchronization');
    }
  };

  const audiences = useSelector(selectAudienceSorted);
  const allContactsAudience = audiences.find(a => a.name === 'All contacts');

  const [stateStateSynchronize, setStateSynchronizeAutomatically] = useState(
    adAudience?.synchronizeAutomatically,
  );
  const [stateAudienceId, setStateAudienceId] = useState(
    allContactsAudience?.id,
  );

  const handleCreate = async () => {
    const [selectedAdAccount] = selectedCredential;

    try {
      const res = await googleAdsCRUDActions.createGoogleAdAudience(
        facebookPageId,
        {
          name,
          audienceId: widgetId && widget ? undefined : stateAudienceId,
          googleClientCredentialId: selectedAdAccount.id,
          synchronizeAutomatically: stateStateSynchronize,
          description: descriprtion?.trim() || name,
        },
      );

      const { id } = res.adAudience;

      if (id) {
        setAdAudienceName('');
        setAdAudienceId(id);
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (adAudienceRemoteId) {
      googleAdsCRUDActions
        .retrieveAudienceById(facebookPageId, adAudienceRemoteId, true)
        .catch(() => {
          errorToast(
            `Couldn't retrieve Google Ads Audience ${adAudienceRemoteId}`,
          );
        });
    }
  }, [adAudienceRemoteId]);

  const isFromAutomation = !!(widgets && widgetId);

  const googleAdsCRUDActions = useGoogleAdsCRUDActions();

  useEffect(() => {
    (async () => {
      await googleAdsCRUDActions.retrieveGoogleClientCredentials(
        activeFbPageId,
      );
    })();
  }, []);

  if (showConfigure) {
    return <GoogleAds />;
  }

  if (!adAudience) {
    return (
      <SlideoutMenu className={styles.menu} setRef={setRef}>
        <div className={styles.headerContent}>
          <Heading className={styles.headerTitle} size={4}>
            Create Google Ads Audience
          </Heading>

          <div className={styles.headerButtons}>
            <Button
              disabled={
                !name.trim().length ||
                !!adAudienceRemoteId ||
                !selectedCredential.length
              }
              onClick={handleCreate}
            >
              Create
            </Button>
            <CloseOutlined className={styles.closeIcon} onClick={onClose} />
          </div>
        </div>

        <SlideoutMenu.Content className={styles.content}>
          <div className={styles.container}>
            <span className={styles.field}>Google Ads Account </span>

            <Select
              className={styles.select}
              hideCount
              isLoading={isLoadingAccounts}
              placeholder="Choose ad account"
              singleSelect
              options={googleClientCredentials.map(v => ({
                ...v,
                value: v.clientId,
                label: renderAdAccountName(v),
              }))}
              selected={selectedCredential.map(v => ({
                ...v,
              }))}
              onChange={v => {
                setSelectedCredential(v);
              }}
            />
          </div>

          <div className={styles.container}>
            <span className={styles.field}>Google audience name</span>

            <TextInput
              className={styles.input}
              placeholder="Audience name"
              value={name}
              onChange={e => setAdAudienceName(e.target.value)}
            />
          </div>

          <div className={styles.container}>
            <span className={styles.field}>Google audience description</span>

            <TextInput
              className={styles.input}
              placeholder="Audience description"
              value={descriprtion}
              onChange={e => setAdAudienceDescription(e.target.value)}
            />
          </div>

          <div className={styles.container}>
            <div className={styles.configArea}>
              {!isFromAutomation && (
                <FacebookAdsAudienceMapping
                  chatbotId={chatbotId}
                  selectedAudienceId={stateAudienceId}
                  onAudienceChange={audienceId =>
                    setStateAudienceId(audienceId)
                  }
                />
              )}
              <div className={styles.options}>
                <Radio
                  checked={stateStateSynchronize}
                  onChange={e => {
                    setStateSynchronizeAutomatically(e.target.checked);
                  }}
                >
                  Automatically
                </Radio>
                <Radio
                  checked={!stateStateSynchronize}
                  disabled={isFromAutomation}
                  onChange={e => {
                    setStateSynchronizeAutomatically(!e.target.checked);
                  }}
                >
                  Manually
                </Radio>
              </div>
            </div>
          </div>
        </SlideoutMenu.Content>
      </SlideoutMenu>
    );
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        <div>
          <Heading className={styles.headerTitle} size={4}>
            Sync to Google Audience
          </Heading>
          <Heading className={styles.headerSubtitle} size={6}>
            {adAudience.name}
          </Heading>
        </div>

        <div className={styles.headerButtons}>
          {!isFromAutomation && (
            <Button
              css={{ container: styles.btn }}
              onClick={() => handleSyncNow(adAudience)}
            >
              Sync to Google Now
            </Button>
          )}
          <CloseOutlined className={styles.closeIcon} onClick={onClose} />
        </div>
      </div>

      <SlideoutMenu.Content className={styles.content}>
        <div className={styles.container}>
          <div className={styles.configArea}>
            {!isFromAutomation && (
              <FacebookAdsAudienceMapping
                chatbotId={chatbotId}
                selectedAudienceId={adAudience?.audience?.id}
                selectedAdAudienceId={connection?.adAudienceId}
                onAudienceChange={handleAudienceChange}
              />
            )}
            <div className={styles.options}>
              <Radio
                checked={adAudience.synchronizeAutomatically}
                onChange={e => {
                  handleStatusChange(e.target.checked);
                }}
              >
                Automatically
              </Radio>
              <Radio
                checked={!adAudience.synchronizeAutomatically}
                disabled={isFromAutomation}
                onChange={e => {
                  handleStatusChange(!e.target.checked);
                }}
              >
                Manually
              </Radio>
            </div>
            <div className={classNames(styles.table, styles.twoColumns)}>
              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Name
                </Heading>
                <span className={styles.value}>{adAudience.name}</span>
              </div>

              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Approximate Audience Size
                </Heading>
                <span className={styles.value}>
                  {[
                    adAudience.approximateCountLowerBound,
                    adAudience.approximateCountUpperBound,
                  ].every(isNumber)
                    ? `${adAudience.approximateCountLowerBound} - ${adAudience.approximateCountUpperBound}`
                    : '-'}
                </span>
              </div>

              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Created
                </Heading>
                <span className={styles.value}>
                  {moment(adAudience.createdAt).format('MM/DD/YYYY HH:mm')}
                </span>
              </div>

              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Ad Account
                </Heading>
                <span className={styles.value}>
                  {adAudience.googleClientCredential
                    ? renderAdAccountName(adAudience?.googleClientCredential)
                    : '-'}
                </span>
              </div>

              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Account Token Owner
                </Heading>
                <span className={styles.value}>
                  {adAudience?.googleClientCredential?.user?.email || '-'}
                </span>
              </div>

              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Status
                </Heading>
                <span className={styles.value}>
                  {renderAdAudienceStatus(adAudience)}
                </span>
              </div>

              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Synchronization Errors
                </Heading>
                <span className={classNames(styles.value, styles.wrap)}>
                  {renderAdAudienceSyncError(adAudience) ? (
                    <code>{renderAdAudienceSyncError(adAudience)}</code>
                  ) : (
                    '-'
                  )}
                </span>
              </div>

              {connection && (
                <div className={styles.row}>
                  <Heading className={styles.cardTitle} size={5}>
                    Webhook status
                  </Heading>
                  <span className={styles.value}>
                    <Status
                      status={connection.active ? 'Active' : 'Not active'}
                      color={
                        connection.active ? colors.green : colors.grayLight
                      }
                    />
                  </span>
                </div>
              )}

              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Last Synchronization Time
                </Heading>
                <span className={styles.value}>
                  {adAudience?.lastSynchronizationTime
                    ? moment(adAudience.lastSynchronizationTime).format(
                        'MM/DD/YYY HH:mm',
                      )
                    : '-'}
                </span>
              </div>
            </div>

            {!!adAudience.remoteCampaigns?.length && (
              <div className={classNames(styles.table, styles.threeColumns)}>
                <div className={styles.header}>
                  <Heading className={styles.cardTitle} size={5}>
                    Campaign ID
                  </Heading>
                  <Heading className={styles.cardTitle} size={5}>
                    Status
                  </Heading>
                  <Heading className={styles.cardTitle} size={5}>
                    Last updated
                  </Heading>
                </div>
                {adAudience.remoteCampaigns.map(adCampaign => (
                  <div className={styles.row} key={adCampaign.id}>
                    <span className={styles.value} title={adCampaign.name}>
                      {adCampaign.id}
                    </span>

                    <span className={styles.value}>
                      {renderAdCampaignStatus(adCampaign.status)}
                    </span>

                    <span className={styles.value}>
                      {adCampaign.updatedTime
                        ? formatDateTime(
                            adCampaign.updatedTime,
                            'MM/DD/YYYY, h:mm:ss a',
                          )
                        : '-'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className={classNames(styles.table, styles.twoColumns)}>
              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Visitor Rows Sent
                </Heading>
                <span className={styles.value}>
                  {adAudience.exportedVisitorsCount || 0}
                </span>
              </div>
              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Email Rows Sent
                </Heading>
                <span className={styles.value}>
                  {adAudience.exportedEmailsCount || 0}
                </span>
              </div>
              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Page-Scoped User ID Rows Sent
                </Heading>
                <span className={styles.value}>
                  {adAudience.exportedPageuidsCount || 0}
                </span>
              </div>

              <div className={styles.row}>
                <Heading className={styles.cardTitle} size={5}>
                  Total Rows Sent
                </Heading>
                <span className={styles.value}>
                  {(adAudience.exportedVisitorsCount || 0) +
                    (adAudience.exportedEmailsCount || 0) +
                    (adAudience.exportedPageuidsCount || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
